// API client for Hercules Portal
// Uses Firebase auth token for authenticated requests
// Token auto-refreshes via Firebase SDK (never expires for active sessions)

import { auth } from './firebase';

// Production: VITE_API_BASE_URL=https://api.hercules.live/api
// Development: Uses Vite proxy (empty string falls back to /api)
export const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: FormData | Record<string, unknown>;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  status: number;
  detail?: string;

  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.status = status;
    this.detail = detail;
    this.name = 'ApiError';
  }
}

export async function apiRequest<T = void>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  // Get fresh token from Firebase SDK (auto-refreshes expired tokens)
  const token = await auth.currentUser?.getIdToken();

  const requestHeaders: Record<string, string> = {
    ...headers,
  };

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  // View-as-client: admin impersonation
  const viewAs = localStorage.getItem('viewAsClientId');
  if (viewAs) {
    requestHeaders['X-View-As-Client'] = viewAs;
  }

  let requestBody: FormData | string | undefined;

  if (body) {
    if (body instanceof FormData) {
      requestBody = body;
      // Don't set Content-Type for FormData - browser will set it with boundary
    } else {
      requestHeaders['Content-Type'] = 'application/json';
      requestBody = JSON.stringify(body);
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: requestHeaders,
    body: requestBody,
  });

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login';
      throw new ApiError(401, 'Session expired', 'Please log in again');
    }

    let errorDetail: string | undefined;
    try {
      const errorBody = await response.json();
      errorDetail = errorBody.detail || errorBody.message;
    } catch {
      // Ignore JSON parse errors
    }
    throw new ApiError(
      response.status,
      `API request failed: ${response.statusText}`,
      errorDetail,
    );
  }

  // 204 No Content — server explicitly says "no body" (e.g. DELETE)
  if (response.status === 204) {
    return undefined as T;
  }

  // Parse JSON body
  const text = await response.text();
  if (!text) {
    throw new ApiError(
      response.status,
      'Unexpected empty response body',
      'Server returned success status but no data',
    );
  }

  return JSON.parse(text) as T;
}

export { ApiError };
